import assert from 'assert';

import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureMethodExecutionInstanceStatus,
  ProcedureRunDataEntryMode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AccessibleGraphQLObject, getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {ExperimentalInputEvaluatedValues} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import sortedBy from 'util/sortedBy';

import {InaccessibleInfoGraphQL} from '../../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {NOT_STARTED_PMEI_STATUSES} from '../execution-status/activeMethodUtils';
import {MethodExecutionLimitState} from '../execution-status/useMethodExecutionLimitState';

export function areProcedureRunInputsEvaluatedValuesValid(
  evaluatedValues: ExperimentalInputEvaluatedValues
): boolean {
  return Object.values(evaluatedValues).every((methodData) => methodData.blockingValueErrorCount === 0);
}

/* True if conditions are valid for creation, false otherwise. */
export function validateConditions(conditions: Array<BaseCondition>): boolean {
  const conditionsValid = !conditions.some(
    (condition) =>
      condition.validationState?.isNameLengthOverLimit ||
      condition.validationState?.isDuplicateName ||
      condition.validationState?.isDescLengthOverLimit ||
      (condition.numReplicates < 1 && !isPersistedCondition(condition))
  );
  return conditionsValid;
}

function hasDraftConditionWithZeroReplicates(conditions: Array<BaseCondition>): boolean {
  return conditions.some((condition) => condition.numReplicates < 1 && !isPersistedCondition(condition));
}

/** Shown when methods disagree, so the shared table cannot be edited, saved, or promoted. */
export const NON_UNIFORM_PLAN_CONDITIONS_SAVE_TOOLTIP =
  'Conditions differ between unit operations and cannot be edited here.';

/**
 * Returns a tooltip string describing why conditions are invalid, or null if they are valid.
 * Used to show the reason why the Save button is disabled.
 */
export function getConditionValidationTooltip(conditions: Array<BaseCondition>): string | null {
  const hasDuplicateNames = conditions.some(({validationState}) => validationState?.isDuplicateName);
  const hasNameTooLong = conditions.some(({validationState}) => validationState?.isNameLengthOverLimit);
  const hasDescTooLong = conditions.some(({validationState}) => validationState?.isDescLengthOverLimit);
  const hasZeroReplicates = hasDraftConditionWithZeroReplicates(conditions);

  const reasons: Array<string> = [];
  if (hasDuplicateNames) {
    reasons.push('duplicate condition names');
  }
  if (hasNameTooLong) {
    reasons.push('condition name is too long');
  }
  if (hasDescTooLong) {
    reasons.push('condition description is too long');
  }
  if (hasZeroReplicates) {
    reasons.push('condition must have at least one replicate');
  }

  if (reasons.length === 0) {
    return null;
  }

  return `Cannot save: ${reasons.join(', ')}`;
}

export function isNumConditionReplicatesValid(methodExecutionLimitState: MethodExecutionLimitState): boolean {
  return !methodExecutionLimitState.isOverLimit;
}

interface ParallelExecutionMethodExecutionInstance {
  id: string;
  status: ProcedureMethodExecutionInstanceStatus | null;
  templateMethodVersion: AccessibleGraphQLObject | InaccessibleInfoGraphQL;
  methodTask: AccessibleGraphQLObject | InaccessibleInfoGraphQL | null;
  procedureRunConditionReplicate: {archiveData: {archived: boolean}};
}

/**
 * Returns the first reason a parallel execution run can't add or remove the given condition-replicates, with
 * the backend's message, or null if it can. `methodNameById` lists every unit operation of the run. Archived
 * method execution instances stay on their worksheets, so they count toward whether a worksheet has started,
 * but not as live condition-replicates. Only the backend rejects runs with several worksheets per unit
 * operation, which only the legacy add-condition flow creates.
 */
export function getParallelExecutionReplicateChangeError({
  defaultDataEntryMode,
  methodExecutionInstances,
  methodNameById,
  addedMethodIds,
  removedMethodExecutionInstanceIds,
  isSplitPoolEnabled,
}: {
  defaultDataEntryMode: ProcedureRunDataEntryMode;
  methodExecutionInstances: ReadonlyArray<ParallelExecutionMethodExecutionInstance>;
  methodNameById: ReadonlyMap<string, string>;
  addedMethodIds: ReadonlyArray<string>;
  removedMethodExecutionInstanceIds: ReadonlyArray<string>;
  isSplitPoolEnabled: boolean;
}): string | null {
  if (
    !isSplitPoolEnabled ||
    defaultDataEntryMode !== ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER ||
    (addedMethodIds.length === 0 && removedMethodExecutionInstanceIds.length === 0)
  ) {
    return null;
  }
  const getMethodId = (pmei: ParallelExecutionMethodExecutionInstance): string =>
    getAccessibleOrInaccessibleId(pmei.templateMethodVersion);
  const getSortedMethodNames = (methodIds: Iterable<string>): string =>
    sortedBy(
      [...methodIds].map((methodId) => {
        const methodName = methodNameById.get(methodId);
        assert(methodName != null, `Expected method ${methodId} to have a name`);
        return methodName;
      }),
      [(name) => name]
    ).join(', ');

  const removedIds = new Set(removedMethodExecutionInstanceIds);
  const addedMethodIdSet = new Set(addedMethodIds);
  const emptyMethodIds = [...methodNameById.keys()].filter(
    (methodId) =>
      !addedMethodIdSet.has(methodId) &&
      !methodExecutionInstances.some(
        (pmei) =>
          getMethodId(pmei) === methodId &&
          !pmei.procedureRunConditionReplicate.archiveData.archived &&
          !removedIds.has(pmei.id)
      )
  );
  if (emptyMethodIds.length > 0) {
    return `Unit operations must have at least one condition: ${getSortedMethodNames(emptyMethodIds)}.`;
  }

  const changedMethodIds = new Set([
    ...addedMethodIds,
    ...methodExecutionInstances.filter((pmei) => removedIds.has(pmei.id)).map((pmei) => getMethodId(pmei)),
  ]);
  const startedMethodIds = new Set(
    methodExecutionInstances
      .filter(
        (pmei) =>
          pmei.methodTask != null || (pmei.status != null && !NOT_STARTED_PMEI_STATUSES.has(pmei.status))
      )
      .map((pmei) => getMethodId(pmei))
  );
  const changedStartedMethodIds = [...changedMethodIds].filter((methodId) => startedMethodIds.has(methodId));
  if (changedStartedMethodIds.length === 0) {
    return null;
  }
  return (
    'Cannot add or remove condition-replicates in these unit operations because their worksheets have ' +
    `already been mapped or started: ${getSortedMethodNames(changedStartedMethodIds)}.`
  );
}

const CONDITION_SETTINGS_CATEGORY = 'Condition settings';

/**
 * Collects all validation errors for a procedure run plan into ErrorBadgeError objects
 * suitable for display in the header error badge.
 *
 * Errors are categorized by method name or "Condition settings" so the badge popover
 * groups them under the appropriate headings.
 */
export function collectPlanErrors({
  evaluatedValues,
  methodExecutionLimitState,
  conditions,
  methodIdToName,
  conditionsText,
  hasNewerVersion,
  procedureString,
}: {
  evaluatedValues: ExperimentalInputEvaluatedValues;
  methodExecutionLimitState: MethodExecutionLimitState;
  conditions: Array<BaseCondition>;
  methodIdToName: Record<string, string>;
  conditionsText: string;
  hasNewerVersion: boolean;
  procedureString: string;
}): Array<ErrorBadgeError> {
  const errors: Array<ErrorBadgeError> = [];

  if (hasNewerVersion) {
    errors.push({
      message: `A new version of this ${procedureString} has been ${BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING ? 'published' : 'created'}`,
      category: 'Version Update Required',
    });
  }

  // Blocking value errors in evaluated values, grouped by method name.
  for (const [methodId, values] of Object.entries(evaluatedValues)) {
    if (values.blockingValueErrorCount > 0) {
      const category = methodIdToName[methodId] ?? '';
      assert(category !== '', `Expected method ${methodId} to have a name`);
      errors.push({
        message: `Some ${conditionsText} have invalid values specified`,
        category,
        count: values.blockingValueErrorCount,
      });
    }
  }

  // Condition-replicate limit exceeded
  if (methodExecutionLimitState.isOverLimit) {
    errors.push({
      message: `Cannot exceed ${methodExecutionLimitState.replicateLimit} total replicates, reduce number of ${conditionsText} or replicates`,
      category: CONDITION_SETTINGS_CATEGORY,
    });
  }

  // Condition name/description validation errors
  for (const condition of conditions) {
    if (condition.numReplicates < 1 && !isPersistedCondition(condition)) {
      errors.push({
        message: `${condition.name} must have at least one replicate`,
        category: CONDITION_SETTINGS_CATEGORY,
      });
    }
    if (condition.validationState?.isDuplicateName) {
      errors.push({message: `Duplicate name: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isNameLengthOverLimit) {
      errors.push({message: `Name is too long: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isDescLengthOverLimit) {
      errors.push({
        message: `Condition description is too long: ${condition.name}`,
        category: CONDITION_SETTINGS_CATEGORY,
      });
    }
  }

  return errors;
}
